/**
 * WalletModalManager — Singleton Modal State
 * ============================================
 * Because the wallet button may be rendered multiple times (header, mobile, etc.),
 * this provides a singleton so only one "owns" the modal.
 *
 * HOW TO USE:
 * const { isOpen, open, close, isOwner } = useWalletModal();
 * Only render <WalletSelectionModal> when `isOwner` is true.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

// ─── Singleton state (lives outside React) ──────────────────────────────────

let _isOpen = false;
let _ownerId: string | null = null;
let _listeners: Set<() => void> = new Set();

function notify() {
  _listeners.forEach((fn) => fn());
}

function openModal(ownerId: string) {
  // If already open by a different owner, ignore
  if (_isOpen && _ownerId !== ownerId) return;
  _isOpen = true;
  _ownerId = ownerId;
  notify();
}

function closeModal() {
  _isOpen = false;
  _ownerId = null;
  notify();
}

function subscribe(listener: () => void) {
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
}

// ─── React hook ─────────────────────────────────────────────────────────────

let _instanceCounter = 0;

export function useWalletModal() {
  // Each hook call gets a stable instance ID
  const idRef = useRef<string>(`wallet-modal-${++_instanceCounter}`);
  const instanceId = idRef.current;

  const [, forceRender] = useState(0);

  useEffect(() => {
    return subscribe(() => forceRender((n) => n + 1));
  }, []);

  const open = useCallback(() => {
    openModal(instanceId);
  }, [instanceId]);

  const close = useCallback(() => {
    closeModal();
  }, []);

  return {
    /** Whether the modal should be shown */
    isOpen: _isOpen,
    /** Whether THIS instance is the modal owner (only owner should render the modal) */
    isOwner: _ownerId === instanceId,
    /** Open the modal (claims ownership for this instance) */
    open,
    /** Close the modal (releases ownership) */
    close,
  };
}
